/*
 * if there is no "read permission" for main controller, we will redirect to 403
 * boolean parameter means, its the main(required for the page) permission for that spesific page.
 */
export const permissionMap = {
  // Advertiser leads contain contact details. Give this queue its own staff grant.
  ads_inquiries: { ads_inquiries: true },
  // Creative review is a write-only staff grant; a read grant must not open it.
  ads_review: { ads_review: "write" },
  // Finance projections are limited to the separate finance write grant.
  ads_finance: { ads_finance: "write" },
  ads_operations: { ads_operations: "write" },
  // Policy staff only: reinstating a FAST ad takedown (not ads operations).
  ads_policy: { ads_policy: "write" },
  // Dedicated provider operations read; no other Ads grant opens this route.
  ads_provider_ops: { ads_provider_ops: true },
  // Playable review opens to ads_review OR ads_operations (contract A8); deciding needs ads_review (checked on screen).
  ads_playable_review: { ads_review: false, ads_operations: false },
  // Ads settings and approvals open to ads_finance OR ads_operations (read or write); each change is gated on screen and in core.
  ads_staff_settings: { ads_finance: false, ads_operations: false },
  // Revenue share: ads_finance read; changes need write (checked on screen and in core).
  ads_revenue_share: { ads_finance: true },
  // Team access (contract §14): ads_operations read or write sees the team and audit; lock needs write (on screen and in core).
  ads_team_access: { ads_operations: false },
  // Authenticator resets: the ads_account_security page. It starts with no role granted; release seeds the grant once Josh
  // decides who approves resets. Opening and approving need write.
  ads_account_security: { ads_account_security: false },
  // Podcast ads: spot review opens to ads_review OR ads_operations; shows and breaks need ads_operations (checked on screen).
  ads_podcast: { ads_review: false, ads_operations: false },
  game_editor: { configurations: true },
  hero_editor: { configurations: true, movies: false, series: false, music: false, music_video: false, podcast: false, sync_rooms: false },
  discover_movies: { movies: true, series: false },
  discover_tv: { series: true, movies: false },
  // `moderation` is injected but not required: it gates the staff end, suspend,
  // member kick and speaking controls and the report history on this screen.
  sync_rooms: {
    sync_rooms: true,
    moderation: false,
  },
  audit_log: {
    audit_log: true,
  },
  dashboard: {
    reports: true,
  },
  content_dashboard: {
    reports: true,
  },
  // The admin analytics dashboard (Overview/Content/Revenue/Integrity).
  // Same `reports` ability as the dashboards above - it is platform-wide
  // reporting, not a screen of its own content type.
  analytics: {
    reports: true,
  },
  panelUsers: {
    panel_user_informations: true, // Main controller for panel users
  },
  createPanelUser: {
    panel_user_informations: true, // Main controller for panel users
  },
  editPanelUSer: {
    panel_user_informations: true, // Main controller for panel users
  },
  music: {
    music: true,
    // The Genre mapping screen's picker (`genre/listfordropdown`) needs
    // `settings:read`, a different ability from `music` itself - not
    // required to open the page, only read so the screen can tell whether
    // to show the picker or a message (authoring.md §5, batch B6).
    settings: false,
    reports: false,
  },
  music_video: {
    music_video: true,
    reports: false,
  },
  // Label accounts and relations (authoring.md §4). No screen routes to this
  // yet (batch B1 builds only Releases) - added now so the role_pages row
  // klooma_db_api's `2026_09_18_002200` migration grants is assignable ahead
  // of the screen, per klooma-admin-api.md "Deploy order".
  labels: {
    labels: true,
    reports: false,
  },
  // Also guards the catalogue's new movie-authoring screens (list, create,
  // and every /movies/edit/:id/:section route) - same ability, new record.
  movies: {
    movies: true,
    settings: false,
    reports: false,
  },
  series: {
    series: true,
    settings: false,
    reports: false,
  },

  // The Users list and a user's detail page. Everything after `customers` is
  // injected but not required: each one only shows or hides a detail tab
  // (Billing, Creator, Moderation) or a control on it.
  customers: {
    customers: true, // Main controller for customers
    moderation: false,
    billing: false,
    fan_club: false,
  },

  admins: {
    admins: true, // Main controller for customers
  },

  roles: {
    roles: true,
  },

  // The Settings section. `settings` guards it; `configurations` is injected
  // but not required, so an admin with one and not the other still gets in and
  // simply sees fewer tabs. `/settings/configurations` enters under the
  // `configurations` entry below for the same reason, the other way round.
  settings: {
    settings: true,
    configurations: false,
  },
  // The transcoding operations screen. Its own ability rather than a content
  // one: it shows every domain at once, including feed, and exposes source
  // paths, error codes and a retry button.
  media_jobs: {
    media_jobs: true,
  },
  // The abuse-report queue. Its own ability: it exposes members' reports and
  // notes, and its actions end in suspending or banning a member.
  moderation: {
    moderation: true,
    // Optional: only links a reported video letter to its review.
    fan_letter_review: false,
  },
  // Fan club dashboard: membership stats and the platform-wide price.
  fan_club: {
    fan_club: true,
  },
  // Klooma subscription dashboard: membership stats and each plan's price.
  klooma_subscription: {
    klooma_subscription: true,
  },
  translations: {
    translations: true,
  },
  faq: {
    faq: true,
  },
  configurations: {
    configurations: true,
    settings: false,
  },
  join_the_network: {
    join_the_network: true,
  },
  contact_forms: {
    contact_forms: true,
    customers: true,
  },
  newsletter: {
    newsletter: true,
  },
  subscription: {
    subscription: true,
  },
  mail: {
    mail: true,
  },
  sony_ingestion: {
    reports: true,
  },
  // The podcast record's authoring screens. `settings` for the genre picker
  // (`genre/listfordropdown`); `podcast_review` gates take-down and restore
  // on the show and episode editors.
  podcast: {
    podcast: true,
    settings: false,
    podcast_review: false,
  },
  // The member-submission review queue (PRD §3.3). Its own ability; adding a
  // warn/suspend/ban to a rejection also needs `moderation` write.
  podcast_review: {
    podcast_review: true,
    moderation: false,
    podcast: false,
  },
  // The fan letter video review queue (fan letter video spec §7). Its own
  // ability; adding a warn/suspend/ban to a rejection also needs
  // `moderation` write.
  fan_letter_review: {
    fan_letter_review: true,
    moderation: false,
  },
  // "Become a podcaster" applications. Approving grants the account type.
  podcaster_applications: {
    podcaster_applications: true,
    customers: false,
  },
  live: {
    live: true,
  },
  // `/live`, the old Klooma events address: open to a holder of either page,
  // and the screen itself redirects to the Live schedule or shows the list.
  live_entry: {
    live: false,
    scheduled_lives: false,
  },
  // The Live schedule: creator Lives (`scheduled_lives`, required) and Klooma
  // events (`live`, optional - it decides whether events show and can be
  // edited there).
  scheduled_lives: {
    scheduled_lives: true,
    live: false,
  },
  // Every open room at once. Sync rooms and Lives are optional: each card links
  // to its room page only for an admin who can read that page.
  rooms_overview: { rooms_overview: true, sync_rooms: false, lives: false },
  // Live & Sync analytics. Report counts need `moderation`, checked by the API.
  live_sync_analytics: { live_sync_analytics: true, sync_rooms: false, lives: false },
  // Live stage moderation (party gateway). Not `live`, which is the concert schedule.
  lives: { lives: true, moderation: false },
  // The Sync room and Live report queue. Moderation is required; the screen
  // then needs Sync rooms or Lives, and shows each kind only with its page.
  room_reports: { moderation: true, sync_rooms: false, lives: false },
  // FAST channels: the list and each channel's settings page. Write covers
  // every field, publishing and archiving (archive is a reversible status).
  // `channel_sources` is optional: feed source details and controls on the
  // Feed tab. The API's `can_view_sources`/`can_manage` decide what shows.
  channels: { channels: true, channel_sources: false, channel_analytics: false },
  // The FAST Content Library. `rights` is optional: with its write the title
  // drawer offers the FAST right and blackout controls; without it they are
  // read-only.
  content_library: { content_library: true, rights: false },
  // The FAST scheduler. Write edits drafts and submits them; delete approves,
  // publishes and reverts. The library and channel pages are optional links.
  channel_schedule: { channel_schedule: true, content_library: false, channels: false },
  // FAST channel analytics (read only). `channels` is optional: it links rows to
  // each channel's page and shows the channel tabs on a channel's Analytics tab.
  channel_analytics: { channel_analytics: true, channels: false },
  // FAST playout. Write skips and replaces items and handles alerts; delete
  // (the "emergency" right) puts up the emergency slate or the backup. The
  // API answers which controls the caller may use (`permissions`).
  playout: { playout: true },
  // The FAST program guide: corrections, manual entries, weekly blocks and
  // XMLTV imports. The Schedule and Channels pages are optional links.
  program_guide: { program_guide: true, channels: false, channel_schedule: false },
};
